import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Network, Server, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';

const NetworkReferenceTable = () => {
  const [expandedTables, setExpandedTables] = useState([]);

  // Toggle reference tables
  const toggleTable = (tableId) => {
    if (expandedTables.includes(tableId)) {
      setExpandedTables(expandedTables.filter(id => id !== tableId));
    } else {
      setExpandedTables([...expandedTables, tableId]);
    }
  };

  const sections = [
    {
      id: 'ipv4',
      title: 'IPv4 Subnet Reference',
      description: 'Complete IPv4 subnetting information with network masks, CIDR notation, and host counts.',
      icon: Network,
      color: 'blue',
      tables: [
        {
          id: 'ipv4-table1',
          title: 'IPv4 Class A Subnets',
          headers: ['Prefix', 'Mask', 'Hosts', 'Range'],
          data: Array.from({ length: 8 }, (_, i) => {
            const prefix = 8 + i;
            const mask = `255.${256 - Math.pow(2, 16 - prefix)}.0.0`;
            const hosts = Math.pow(2, 24 - prefix) - 2;
            return [
              `/${prefix}`,
              mask,
              hosts.toLocaleString(),
              `10.0.0.0 - 10.${Math.pow(2, 8 - (prefix - 8)) - 1}.255.255`
            ];
          })
        },
        {
          id: 'ipv4-table2',
          title: 'IPv4 Class B Subnets',
          headers: ['Prefix', 'Mask', 'Hosts', 'Range'],
          data: Array.from({ length: 8 }, (_, i) => {
            const prefix = 16 + i;
            const mask = `255.255.${256 - Math.pow(2, 24 - prefix)}.0`;
            const hosts = Math.pow(2, 24 - prefix) - 2;
            return [
              `/${prefix}`,
              mask,
              hosts.toLocaleString(),
              `172.16.0.0 - 172.31.${Math.pow(2, 8 - (prefix - 16)) - 1}.255`
            ];
          })
        },
        {
          id: 'ipv4-table3',
          title: 'IPv4 Class C Subnets',
          headers: ['Prefix', 'Mask', 'Hosts', 'Range'],
          data: Array.from({ length: 8 }, (_, i) => {
            const prefix = 24 + i;
            const mask = `255.255.255.${256 - Math.pow(2, 32 - prefix)}`;
            const hosts = Math.pow(2, 32 - prefix) - 2;
            return [
              `/${prefix}`,
              mask,
              hosts.toLocaleString(),
              `192.168.0.0 - 192.168.0.${Math.pow(2, 8 - (prefix - 24)) - 1}`
            ];
          })
        }
      ]
    },
    {
      id: 'ipv6',
      title: 'IPv6 Prefix Reference',
      description: 'Common IPv6 prefix lengths and their applications in network design.',
      icon: Server,
      color: 'green',
      tables: [
        {
          id: 'ipv6-table1',
          title: 'IPv6 Common Prefixes',
          headers: ['Prefix', 'Type', 'Description'],
          data: [
            ['/32', 'Global Unicast', 'Typical ISP allocation'],
            ['/48', 'Global Unicast', 'Standard site prefix'],
            ['/56', 'Global Unicast', 'Residential customer allocation'],
            ['/64', 'Global Unicast', 'Standard subnet size'],
            ['/128', 'Global Unicast', 'Single host address']
          ]
        }
      ]
    },
    {
      id: 'special',
      title: 'Special Address Ranges',
      description: 'Reserved and special-purpose IP address ranges.',
      icon: AlertCircle,
      color: 'purple',
      tables: [
        {
          id: 'special-table1',
          title: 'IPv4 Special Ranges',
          headers: ['Range', 'Purpose'],
          data: [
            ['10.0.0.0/8', 'Private network'],
            ['172.16.0.0/12', 'Private network'],
            ['192.168.0.0/16', 'Private network'],
            ['169.254.0.0/16', 'Link-local'],
            ['224.0.0.0/4', 'Multicast'],
            ['240.0.0.0/4', 'Reserved']
          ]
        },
        {
          id: 'special-table2',
          title: 'IPv6 Special Ranges',
          headers: ['Prefix', 'Purpose'],
          data: [
            ['::1/128', 'Loopback'],
            ['fc00::/7', 'Unique local address'],
            ['fe80::/10', 'Link-local'],
            ['ff00::/8', 'Multicast'],
            ['2000::/3', 'Global unicast']
          ]
        }
      ]
    }
  ];

  return (
    <motion.div
      key="reference"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-8"
    >
      <div className="bg-gray-800/50 rounded-xl shadow-2xl p-8 backdrop-blur-sm border border-gray-700">
        <motion.h1 
          className="text-3xl font-bold text-center mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-500"
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
        >
          Network Reference Tables
        </motion.h1>
        
        <motion.p 
          className="text-gray-300 text-center max-w-3xl mx-auto"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          Comprehensive reference data for network engineers. Click on any table to expand and view detailed information.
        </motion.p>
      </div>

      <div className="space-y-6">
        {sections.map((section) => (
          <motion.div 
            key={section.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gray-800/50 rounded-xl shadow-lg border border-gray-700 overflow-hidden"
          >
            <motion.button
              whileHover={{ backgroundColor: 'rgba(55, 65, 81, 0.5)' }}
              onClick={() => toggleTable(section.id)}
              className={`w-full px-6 py-4 flex justify-between items-center text-left ${expandedTables.includes(section.id) ? 'bg-gray-700/50' : ''}`}
            >
              <div className="flex items-center space-x-4">
                <div className={`p-2 rounded-lg bg-${section.color}-600/20`}>
                  <section.icon className={`h-6 w-6 text-${section.color}-400`} />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{section.title}</h3>
                  <p className="text-sm text-gray-400">{section.description}</p>
                </div>
              </div>
              {expandedTables.includes(section.id) ? (
                <ChevronUp className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              )}
            </motion.button>

            <AnimatePresence>
              {expandedTables.includes(section.id) && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.3 }}
                  className="px-6 pb-6 space-y-6"
                >
                  {section.tables.map((table) => (
                    <div key={table.id} className="overflow-hidden rounded-lg border border-gray-700">
                      <div className="bg-gray-900/50 px-4 py-3 border-b border-gray-700">
                        <h4 className="font-medium">{table.title}</h4>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-800/50">
                            <tr>
                              {table.headers.map((header) => (
                                <th key={header} className="px-4 py-3 text-left text-sm font-medium text-gray-300">
                                  {header}
                                </th>
                              ))}
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-700/50">
                            {table.data.map((row, rowIndex) => (
                              <motion.tr
                                key={rowIndex}
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{ delay: rowIndex * 0.05 }}
                                className={rowIndex % 2 === 0 ? 'bg-gray-800/20' : 'bg-gray-800/40'}
                              >
                                {row.map((cell, cellIndex) => (
                                  <td key={cellIndex} className="px-4 py-3 text-sm font-mono">
                                    {cell}
                                  </td>
                                ))}
                              </motion.tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

export default NetworkReferenceTable;